/**
 * Unified application navigation configuration
 *
 * This file serves as the single source of truth for all navigation-related configuration:
 * - Sidebar menu
 * - Route configuration
 * - Breadcrumbs
 * - Page titles
 *
 * Design principles:
 * 1. Single Source of Truth
 * 2. DRY - avoid duplicated configuration
 * 3. Type Safety - end-to-end TypeScript support
 * 4. Maintainability - centralized navigation logic
 */

import {
  ApiOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BarcodeOutlined,
  CalendarOutlined,
  // CalendarOutlined,
  CustomerServiceOutlined,
  DatabaseOutlined,
  // DollarOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  HomeOutlined,
  IdcardOutlined,
  // MedicineBoxOutlined,
  // MessageOutlined,
  SettingOutlined,
  SolutionOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import React from "react";
import { forChargeCodeApi } from "@/config/charge-code-api";
import { getPermissionsByRole } from "@/config/permission/permissions";
import { CHARGE_CODE_V2_STOCK_NAVIGATION } from "@/routers/charge-code-v2-stock.navigation";
import { ERoutesPathname } from "@/routers/config";
import * as LazyComponents from "@/routers/lazyComponents";
import type { NavigationItem } from "@/routers/types";

// ==================== Navigation ====================

export const APP_NAVIGATION: NavigationItem[] = [
  // ==================== Home ====================
  {
    key: "home",
    title: "Home",
    path: ERoutesPathname.home,
    icon: React.createElement(HomeOutlined),
    breadcrumb: "Home",
    component: LazyComponents.HomePage,
    showInSidebar: true,
    order: 1,
  },

  // ==================== Patient ====================
  {
    key: "patient",
    title: "Patient",
    path: ERoutesPathname.patient,
    icon: React.createElement(TeamOutlined),
    breadcrumb: "Patient List",
    component: LazyComponents.PatientPage,
    showInSidebar: true,
    order: 2,
    permissionRoles: getPermissionsByRole("patient"),
    children: [
      {
        key: "patient-consultation",
        title: "",
        path: ERoutesPathname.patientConsultation,
        breadcrumb: "",
        pageTitle: (params) => params.name || params.id || "Consultation",
        component: LazyComponents.ConsultationPage,
        showInSidebar: false,
      },
      {
        key: "patient-registration-edit",
        title: " ",
        path: ERoutesPathname.patientDetail,
        breadcrumb: "",
        pageTitle: (params) => `${params.name || params.id || "Patient"}`,
        component: LazyComponents.PatientEdit,
        permissionRoles: getPermissionsByRole("patient-registration-edit"),
        showInSidebar: false,
      },
      {
        key: "patient-registration-create",
        title: " ",
        path: ERoutesPathname.registration,
        breadcrumb: "",
        pageTitle: "Create Patient",
        component: LazyComponents.PatientCreate,
        showInSidebar: false,
      },
    ],
  },
  {
    key: "patient-registration",
    title: "Patient Registration",
    path: ERoutesPathname.registration,
    icon: React.createElement(IdcardOutlined),
    breadcrumb: "Patient Registration",
    component: LazyComponents.PatientCreate,
    showInSidebar: false,
    order: 3,
  },
  // ==================== Appointment ====================
  {
    key: "appointment",
    title: "Appointment",
    path: ERoutesPathname.appointment,
    icon: React.createElement(CalendarOutlined),
    breadcrumb: "Appointment",
    component: LazyComponents.AppointmentPage,
    permissionRoles: getPermissionsByRole("appointment"),
    showInSidebar: true,
    order: 4,
    children: [
      {
        key: "appointment-pre-registration",
        title: "Pre Registration",
        path: ERoutesPathname.appointmentPreRegistration,
        breadcrumb: "Pre Registration",
        pageTitle: "Pre Registration",
        component: LazyComponents.PreRegistrationPage,
        showInSidebar: false,
      },
    ],
  },
  {
    key: "RAD_LAB_MANAGEMENT",
    title: "Investigation",
    path: ERoutesPathname.radLabManagement,
    icon: React.createElement(SolutionOutlined),
    breadcrumb: "Investigation",
    component: LazyComponents.RadLabManagementPage,
    showInSidebar: true,
    permissionRoles: getPermissionsByRole("rad-lab-management"),
    order: 5,
  },

  // ==================== Queue ====================
  {
    key: "queue",
    title: "Queue",
    path: ERoutesPathname.queue,
    icon: React.createElement(SolutionOutlined),
    breadcrumb: "Queue Management",
    component: LazyComponents.QueuePage,
    showInSidebar: true,
    permissionRoles: getPermissionsByRole("queue-view-list"),
    order: 6,
  },

  // ==================== Invoice ====================
  {
    key: "invoice",
    title: "Invoice",
    path: ERoutesPathname.invoice,
    icon: React.createElement(FileTextOutlined),
    breadcrumb: "Invoice",
    showInSidebar: true,
    order: 7,
    // ECHO-3949: pinned to pre-existing roles (hidden from the archival-only role)
    permissionRoles: getPermissionsByRole("invoice-nav"),
    children: [
      {
        key: "invoice-list",
        title: "Invoice",
        path: ERoutesPathname.invoice,
        breadcrumb: "",
        component: LazyComponents.InvoiceListPage,
        permissionRoles: getPermissionsByRole("invoice-nav"),
      },
      {
        key: "medical-coverage",
        title: "Medical Coverage",
        path: ERoutesPathname.invoiceMedicalCoverage,
        breadcrumb: "Medical Coverage",
        component: LazyComponents.InvoiceMedicalCoveragePage,
        permissionRoles: getPermissionsByRole("invoice-nav"),
        children: [
          {
            key: "invoice-corporate-detail",
            title: "Corporate Invoice",
            path: ERoutesPathname.invoiceCorporateDetail,
            pageTitle: (params) => `Corporate Invoice No. ${params.name || params.id || ""}`,
            breadcrumb: "Corporate Invoice",
            component: LazyComponents.CorporateInvoiceDetailPage,
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("invoice-nav"),
          },
        ],
      },
      // {
      //   key: "corporate-invoice",
      //   title: "Corporate Invoice",
      //   path: ERoutesPathname.invoiceCorporate,
      //   breadcrumb: "",
      //   component: LazyComponents.InvoiceCorporatePage,
      // },
      {
        key: "credit-note",
        title: "Credit Note",
        path: ERoutesPathname.invoiceCreditNote,
        breadcrumb: "Credit Note",
        component: LazyComponents.InvoiceCreditNotePage,
        permissionRoles: getPermissionsByRole("credit-note-list"),
        children: [
          {
            key: "credit-note-detail",
            title: "Credit Note Detail",
            path: ERoutesPathname.invoiceCreditNoteDetail,
            breadcrumb: "Credit Note Detail",
            // pageTitle: (params) => `ID: ${params.name}111`,
            component: LazyComponents.InvoiceCreditNoteDetailPage,
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("credit-note-list"),
          },
        ],
      },
    ],
  },

  // ==================== Inventory ====================
  {
    key: "inventory",
    title: "Inventory",
    path: ERoutesPathname.inventory,
    icon: React.createElement(AppstoreOutlined),
    breadcrumb: "Inventory",
    showInSidebar: true,
    order: 8,
    // ECHO-3949: pinned to pre-existing roles (hidden from the archival-only
    // role); children keep their own lists. The "inventory" page list was too
    // narrow for the parent tab and hid it from nurse/doctor/finance roles.
    permissionRoles: getPermissionsByRole("inventory-nav"),
    children: [
      {
        key: "inventory-list",
        title: "Inventory",
        path: ERoutesPathname.inventory,
        breadcrumb: "Inventory",
        component: LazyComponents.InventoryList,
        permissionRoles: getPermissionsByRole("inventory-view"),
        children: [
          {
            key: "inventory-create",
            title: "Create",
            path: ERoutesPathname.inventoryCreate,
            breadcrumb: "Create",
            pageTitle: "Create",
            component: LazyComponents.InventoryCreatePage,
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("inventory-create"),
          },
          {
            key: "inventory-detail",
            title: "Inventory Detail",
            path: ERoutesPathname.inventoryDetail,
            breadcrumb: "Inventory Detail",
            component: LazyComponents.InventoryDetail,
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("inventory-view"),
          },
        ],
      },
      {
        key: "inventory-adjustment",
        title: "Inventory Adjustment",
        path: ERoutesPathname.inventoryAdjustment,
        breadcrumb: "Inventory Adjustment",
        component: LazyComponents.AdjustmentPage,
        permissionRoles: getPermissionsByRole("inventory-adjustment"),
        children: [
          {
            key: "adjustment-create",
            title: "Create",
            path: ERoutesPathname.inventoryAdjustmentCreate,
            breadcrumb: "Create",
            pageTitle: "Create New Adjustment",
            component: LazyComponents.AdjustmentCreatePage,
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("inventory-adjustment-create"),
          },
          {
            key: "adjustment-edit",
            title: "Edit Adjustment",
            path: ERoutesPathname.inventoryAdjustmentEdit,
            breadcrumb: "Edit Adjustment",
            pageTitle: (params) => `Edit ${params.name || "Adjustment"}`,
            component: LazyComponents.AdjustmentDetail,
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("inventory-adjustment-detail"),
          },
          {
            key: "adjustment-detail",
            title: "Adjustment Detail",
            path: ERoutesPathname.inventoryAdjustmentDetail,
            breadcrumb: "Adjustment Detail",
            pageTitle: (params) => params.name || "Adjustment Detail",
            component: LazyComponents.AdjustmentDetail,
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("inventory-adjustment-detail"),
          },
        ],
      },
      {
        key: "suppliers",
        title: "Suppliers",
        path: ERoutesPathname.suppliers,
        breadcrumb: "Suppliers",
        component: LazyComponents.SuppliersPage,
        permissionRoles: getPermissionsByRole("supplier-view"),
        children: [
          {
            key: "suppliers-create",
            title: "Create",
            path: ERoutesPathname.suppliersCreate,
            breadcrumb: "Create",
            pageTitle: "Create",
            component: LazyComponents.SupplierFormPage,
            permissionRoles: getPermissionsByRole("supplier-create"),
            showInSidebar: false,
          },
          {
            key: "suppliers-edit",
            title: "Edit Supplier",
            path: ERoutesPathname.suppliersEdit,
            breadcrumb: "Edit Supplier",
            pageTitle: (params) => `Edit ${params.name || "Supplier"}`,
            component: LazyComponents.SupplierFormPage,
            permissionRoles: getPermissionsByRole("supplier-edit"),
            showInSidebar: false,
          },
          {
            key: "suppliers-detail",
            title: "Supplier Detail",
            path: ERoutesPathname.suppliersDetail,
            breadcrumb: "Supplier Detail",
            pageTitle: (params) => params.name || "Supplier Detail",
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("supplier-view"),
          },
        ],
      },
      // {
      //   key: "purchase-requisition",
      //   title: "Purchase Requisition",
      //   path: ERoutesPathname.inventoryRequisition,
      //   breadcrumb: "Purchase Requisition",
      //   component: LazyComponents.RequisitionPage,
      //   children: [
      //     {
      //       key: "requisition-create",
      //       title: "Create New",
      //       path: ERoutesPathname.requisitionCreate,
      //       breadcrumb: "Create New",
      //       pageTitle: "Create New Requisition",
      //       showInSidebar: false,
      //     },
      //     {
      //       key: "requisition-edit",
      //       title: "Edit Requisition",
      //       path: ERoutesPathname.requisitionEdit,
      //       breadcrumb: "Edit Requisition",
      //       pageTitle: (params) => `Edit ${params.name || "Requisition"}`,
      //       showInSidebar: false,
      //     },
      //     {
      //       key: "requisition-detail",
      //       title: "Requisition Detail",
      //       path: ERoutesPathname.requisitionDetail,
      //       breadcrumb: "Requisition Detail",
      //       pageTitle: (params) => params.name || "Requisition Detail",
      //       showInSidebar: false,
      //     },
      //   ],
      // },
      {
        key: "delivery-order",
        title: "Delivery Order",
        path: ERoutesPathname.delivery,
        breadcrumb: "Delivery Order",
        component: LazyComponents.DeliveryPage,
        permissionRoles: getPermissionsByRole("delivery-order-list"),
        children: [
          {
            key: "delivery-create",
            title: "Create New",
            path: ERoutesPathname.deliveryCreate,
            breadcrumb: "Create New",
            pageTitle: "Create New Delivery",
            showInSidebar: false,
            component: LazyComponents.DeliveryCreatePage,
            permissionRoles: getPermissionsByRole("delivery-order-create"),
          },
          {
            key: "delivery-edit",
            title: "Edit Delivery",
            path: ERoutesPathname.deliveryEdit,
            breadcrumb: "Edit Delivery",
            pageTitle: (params) => `Edit ${params.name || "Delivery"}`,
            component: LazyComponents.DeliveryEditPage,
            permissionRoles: getPermissionsByRole("delivery-order-edit"),
            showInSidebar: false,
          },
          {
            key: "delivery-detail",
            title: "Delivery Detail",
            path: ERoutesPathname.deliveryDetail,
            breadcrumb: "Delivery Detail",
            pageTitle: (params) => params.name || "Delivery Detail",
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("delivery-order-list"),
          },
        ],
      },
      {
        key: "dispensing",
        title: "Dispensing",
        path: ERoutesPathname.dispensing,
        breadcrumb: "Dispensing",
        component: LazyComponents.DispensingPage,
        showInSidebar: false,
        permissionRoles: getPermissionsByRole("dispensing-order-list"),
        children: [
          {
            key: "dispensing-create",
            title: "Create New",
            path: ERoutesPathname.dispensingCreate,
            breadcrumb: "Create New",
            pageTitle: "Create New",
            component: LazyComponents.DispensingCreatePage,
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("dispensing-order-create"),
          },
          {
            key: "dispensing-edit",
            title: "Edit Dispensing",
            path: ERoutesPathname.dispensingEdit,
            breadcrumb: "",
            pageTitle: (params) => `Edit ${params.name || "Dispensing"}`,
            component: LazyComponents.DispensingEditPage,
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("dispensing-order-list"),
          },
          {
            key: "dispensing-finalize",
            title: "Finalize Dispensing",
            path: ERoutesPathname.dispensingFinalize,
            breadcrumb: "Finalize Dispensing",
            pageTitle: (params) => `Finalize ${params.name || "Dispensing"}`,
            component: LazyComponents.DispensingFinalizePage,
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("dispensing-order-list"),
          },
          {
            key: "dispensing-detail",
            title: "Dispensing Detail",
            path: ERoutesPathname.dispensingDetail,
            breadcrumb: "Dispensing Detail",
            pageTitle: (params) => params.name || "Dispensing Detail",
            showInSidebar: false,
            permissionRoles: getPermissionsByRole("dispensing-order-list"),
          },
        ],
      },
    ],
  },

  // ==================== ERP Integration ====================
  {
    key: "erp-integration",
    title: "ERP Integration",
    path: ERoutesPathname.erpIntegration,
    icon: React.createElement(ApiOutlined),
    breadcrumb: "ERP Integration",
    showInSidebar: true,
    order: 8,
    permissionRoles: getPermissionsByRole("erp-consumption-view"),
    children: [
      {
        key: "erp-eden-consumption",
        title: "Eden Consumption",
        path: ERoutesPathname.erpEdenConsumption,
        breadcrumb: "Eden Consumption",
        pageTitle: "Eden Consumption",
        component: LazyComponents.EdenConsumptionPage,
        permissionRoles: getPermissionsByRole("erp-consumption-view"),
      },
      {
        key: "erp-item-master",
        title: "Item Master",
        path: ERoutesPathname.erpItemMaster,
        breadcrumb: "Item Master",
        pageTitle: "Item Master",
        component: LazyComponents.ItemMasterPage,
        permissionRoles: getPermissionsByRole("erp-item-master-view"),
      },
    ],
  },

  // ==================== Services ====================
  {
    key: "services",
    title: "Services",
    path: ERoutesPathname.services,
    icon: React.createElement(CustomerServiceOutlined),
    breadcrumb: "Services",
    showInSidebar: true,
    order: 9,
    permissionRoles: getPermissionsByRole("services"),
    children: [
      {
        key: "medical-services",
        title: "Medical Service",
        path: ERoutesPathname.medicalServices,
        breadcrumb: "Medical Service",
        component: LazyComponents.MedicalServicesPage,
        permissionRoles: getPermissionsByRole("medical-services"),
        children: [
          {
            key: "medical-services-create",
            title: "Create New",
            path: ERoutesPathname.medicalServicesCreate,
            breadcrumb: "Create New",
            pageTitle: "Create New",
            component: LazyComponents.CreateMedicalService,
            permissionRoles: getPermissionsByRole("medical-services-create"),
            showInSidebar: false,
          },
          {
            key: "medical-services-edit",
            title: "Edit Medical Service",
            path: ERoutesPathname.medicalServicesEdit,
            breadcrumb: "Edit Medical Service",
            pageTitle: (params) => `Edit ${params.name || "Medical Service"}`,
            component: LazyComponents.CreateMedicalService,
            permissionRoles: getPermissionsByRole("medical-services-edit"),
            showInSidebar: false,
          },
        ],
      },
      {
        key: "package",
        title: "Package",
        path: ERoutesPathname.package,
        breadcrumb: "Packages",
        component: LazyComponents.PackagePage,
        permissionRoles: getPermissionsByRole("package"),
        children: [
          {
            key: "package-create",
            title: "Create",
            path: ERoutesPathname.packageCreate,
            breadcrumb: "Create",
            pageTitle: "Create",
            component: LazyComponents.CreatePackage,
            permissionRoles: getPermissionsByRole("package-create"),
            showInSidebar: false,
          },
          {
            key: "package-edit",
            title: "Edit Package",
            path: ERoutesPathname.packageEdit,
            breadcrumb: "Edit Package",
            pageTitle: (params) => `Edit ${params.name || "Package"}`,
            component: LazyComponents.UpdatePackage,
            permissionRoles: getPermissionsByRole("package-edit"),
            showInSidebar: false,
          },
        ],
      },
      {
        key: "package2",
        title: "Packages 2.0",
        path: ERoutesPathname.packagesV2,
        breadcrumb: "Packages 2.0",
        component: LazyComponents.PackagesV2Page,
        permissionRoles: getPermissionsByRole("charge-code-v2"),
        showInSidebar: true,
        children: [
          {
            key: "packages-v2-create",
            title: "Create",
            path: ERoutesPathname.packagesV2Create,
            breadcrumb: "Create",
            pageTitle: "Create",
            component: LazyComponents.CreatePackageV2Page,
            permissionRoles: getPermissionsByRole("charge-code-v2"),
            showInSidebar: false,
          },
          {
            key: "packages-v2-edit",
            title: "Edit Package",
            path: ERoutesPathname.packagesV2Edit,
            breadcrumb: "Edit Package",
            pageTitle: (params) => `Edit ${params.name || "Package"}`,
            component: LazyComponents.CreatePackageV2Page,
            permissionRoles: getPermissionsByRole("charge-code-v2"),
            showInSidebar: false,
          },
        ],
      },
    ],
  },

  // ==================== Charge Codes ====================
  {
    key: "charge-code-management",
    title: "Charge Codes",
    path: ERoutesPathname.chargeCodeManagement,
    icon: React.createElement(BarcodeOutlined),
    breadcrumb: "Charge Code Management",
    showInSidebar: true,
    order: 9,
    component: LazyComponents.ChargeCodeManagementPage,
    permissionRoles: getPermissionsByRole("charge-code-v2"),
    children: [
      {
        key: "charge-code-create",
        title: "Create New",
        path: ERoutesPathname.chargeCodeCreate,
        breadcrumb: "Create New",
        pageTitle: "Create New Charge Code",
        component: LazyComponents.ChargeCodeManagementPage,
        showInSidebar: false,
        permissionRoles: getPermissionsByRole("charge-code-v2"),
      },
      {
        key: "charge-code-detail",
        title: "Charge Code Detail",
        path: ERoutesPathname.chargeCodeDetail,
        breadcrumb: "Charge Code Detail",
        pageTitle: (params) => params.chargeCode || "Charge Code Detail",
        component: LazyComponents.ChargeCodeManagementPage,
        showInSidebar: false,
        permissionRoles: getPermissionsByRole("charge-code-v2"),
      },
    ],
  },

  // ==================== Charge Code V2 stock ====================
  ...forChargeCodeApi<NavigationItem[]>({ v1: [], v2: [CHARGE_CODE_V2_STOCK_NAVIGATION] }),

  // ==================== Pharmacy ====================
  // {
  //   key: "pharmacy",
  //   title: "Pharmacy",
  //   path: ERoutesPathname.pharmacy,
  //   icon: React.createElement(MedicineBoxOutlined),
  //   breadcrumb: "Pharmacy",
  //   component: LazyComponents.PharmacyPage,
  //   showInSidebar: true,
  //   order: 9,
  //   children: [
  //     {
  //       key: "pharmacy-detail",
  //       title: "Pharmacy Detail",
  //       path: ERoutesPathname.pharmacyDetail,
  //       breadcrumb: "Pharmacy Detail",
  //       pageTitle: (params) => params.name || "Pharmacy Detail",
  //       showInSidebar: false,
  //     },
  //     {
  //       key: "pharmacy-item",
  //       title: "Pharmacy Item",
  //       path: ERoutesPathname.pharmacyItem,
  //       breadcrumb: "Pharmacy Item",
  //       pageTitle: (params) => params.name || "Pharmacy Item",
  //       showInSidebar: false,
  //     },
  //   ],
  // },

  // ==================== Investigations ====================
  {
    key: "investigations",
    title: "Investigations",
    path: ERoutesPathname.investigations,
    icon: React.createElement(ExperimentOutlined),
    breadcrumb: "Investigations",
    component: LazyComponents.InvestigationsPage,
    showInSidebar: false,
    order: 10,
  },

  // ==================== Reports ====================
  {
    key: "reports",
    title: "Reports",
    path: ERoutesPathname.reports,
    icon: React.createElement(BarChartOutlined),
    breadcrumb: "Reports",
    component: LazyComponents.ReportsPage,
    showInSidebar: true,
    order: 11,
    isPlaceholder: true, // Placeholder indicator
    // ECHO-3949: pinned to all pre-existing roles (hidden from the archival-only role)
    permissionRoles: getPermissionsByRole("reports"),
  },

  // ==================== ECHO 1.0 Archive (ECHO-3859) ====================
  {
    key: "legacy-archive",
    title: "ECHO 1.0 Archive - TSPRG",
    path: ERoutesPathname.legacyArchive,
    icon: React.createElement(DatabaseOutlined),
    breadcrumb: "ECHO 1.0 Archive - TSPRG",
    component: LazyComponents.LegacyArchivePage,
    showInSidebar: true,
    order: 12,
    // ECHO-3950: System Admin + TSPRG archival role only
    permissionRoles: getPermissionsByRole("legacy-archive"),
  },

  // ==================== Campaign ====================
  // {
  //   key: "campaign",
  //   title: "Campaign",
  //   path: ERoutesPathname.campaign,
  //   icon: React.createElement(DollarOutlined),
  //   breadcrumb: "Campaign",
  //   showInSidebar: true,
  //   order: 12,
  //   isPlaceholder: true, // Placeholder indicator
  // },

  // ==================== Task ====================
  // {
  //   key: "task",
  //   title: "Task",
  //   path: ERoutesPathname.task,
  //   icon: React.createElement(MessageOutlined),
  //   breadcrumb: "Tasks",
  //   showInSidebar: true,
  //   order: 13,
  //   isPlaceholder: true, // Placeholder indicator
  // },

  // ==================== Settings ====================
  {
    key: "settings",
    title: "Settings",
    path: ERoutesPathname.settings,
    icon: React.createElement(SettingOutlined),
    breadcrumb: "Settings",
    showInSidebar: true,
    permissionRoles: getPermissionsByRole("settings"),
    order: 14,
    children: [
      {
        key: "medical-template",
        title: "Medical Template",
        path: ERoutesPathname.medicalTemplate,
        breadcrumb: "Medical Template",
        component: LazyComponents.SettingsListPage,
        permissionRoles: getPermissionsByRole("medical-template"),
        children: [
          {
            key: "medical-template-create",
            title: "Create New",
            path: ERoutesPathname.medicalTemplateCreate,
            breadcrumb: "Create New",
            pageTitle: "Create New Template",
            component: LazyComponents.SettingsDetailPage,
            permissionRoles: getPermissionsByRole("medical-template-create"),
            showInSidebar: false,
          },
          {
            key: "medical-template-edit",
            title: "Edit Template",
            path: ERoutesPathname.medicalTemplateEdit,
            breadcrumb: "Edit Template",
            pageTitle: (params) => `Edit ${params.name || "Template"}`,
            component: LazyComponents.SettingsDetailPage,
            permissionRoles: getPermissionsByRole("medical-template-edit"),
            showInSidebar: false,
          },
        ],
      },
      {
        key: "discharge-advice",
        title: "Discharge Advice",
        path: ERoutesPathname.dischargeAdvice,
        breadcrumb: "Discharge Advice",
        component: LazyComponents.DischargeAdviceListPage,
        permissionRoles: getPermissionsByRole("settings"),
        children: [
          {
            key: "discharge-advice-create",
            title: "Create New",
            path: ERoutesPathname.dischargeAdviceCreate,
            breadcrumb: "Create New",
            pageTitle: "Create New Discharge Advice",
            component: LazyComponents.DischargeAdviceDetailPage,
            permissionRoles: getPermissionsByRole("settings"),
            showInSidebar: false,
          },
          {
            key: "discharge-advice-edit",
            title: "Edit",
            path: ERoutesPathname.dischargeAdviceEdit,
            breadcrumb: "Edit",
            pageTitle: (params) => `Edit ${params.name || "Discharge Advice"}`,
            component: LazyComponents.DischargeAdviceDetailPage,
            permissionRoles: getPermissionsByRole("settings"),
            showInSidebar: false,
          },
        ],
      },
      {
        key: "system-preferences",
        title: "System Preferences",
        path: ERoutesPathname.systemPreferences,
        breadcrumb: "System Preferences",
        component: LazyComponents.SystemPreferencesListPage,
        permissionRoles: getPermissionsByRole("system-preferences"),
        children: [
          // Vendor management routes under System Preferences
          {
            key: "system-preferences-vendor-create",
            title: "Create Vendor",
            path: ERoutesPathname.systemPreferencesVendorCreate,
            breadcrumb: "Create Vendor",
            pageTitle: "Create Vendor",
            component: LazyComponents.VendorForm,
            showInSidebar: false,
          },
          {
            key: "system-preferences-vendor-edit",
            title: "Edit Vendor",
            path: ERoutesPathname.systemPreferencesVendorEdit,
            breadcrumb: "Edit Vendor",
            pageTitle: (params) => `Edit ${params.name || "Vendor"}`,
            component: LazyComponents.VendorForm,
            showInSidebar: false,
          },
          {
            key: "system-preferences-vendor-detail",
            title: "Vendor Detail",
            path: ERoutesPathname.systemPreferencesVendorDetail,
            breadcrumb: "Vendor Detail",
            pageTitle: (params) => params.name || "Vendor Detail",
            showInSidebar: false,
          },
        ],
      },
      {
        key: "user-management",
        title: "User Management",
        path: ERoutesPathname.userManagement,
        breadcrumb: "User Management",
        component: LazyComponents.UserManagementListPage,
        permissionRoles: getPermissionsByRole("user-management"),
        children: [
          {
            key: "user-management-create",
            title: "Create New",
            path: ERoutesPathname.userManagementCreate,
            breadcrumb: "Create New",
            pageTitle: "Create New",
            component: LazyComponents.CreateUserPage,
            permissionRoles: getPermissionsByRole("user-management-create"),
            showInSidebar: false,
          },
          {
            key: "user-management-edit",
            title: "Edit User",
            path: ERoutesPathname.userManagementEdit,
            breadcrumb: "Edit User",
            pageTitle: (params) => `Edit ${params.name || "User"}`,
            component: LazyComponents.EditUserPage,
            permissionRoles: getPermissionsByRole("user-management-edit"),
            showInSidebar: false,
          },
        ],
      },
      {
        key: "role-management",
        title: "Role & Role Group Management",
        path: ERoutesPathname.roleManagement,
        breadcrumb: "Role & Role Group Management",
        component: LazyComponents.RoleManagementPage,
        permissionRoles: getPermissionsByRole("role-management"),
      },
      {
        key: "coverage",
        title: "Coverage",
        path: ERoutesPathname.medicalCoverage,
        breadcrumb: "Medical Coverage",
        component: LazyComponents.MedicalCoverage,
        showInSidebar: false,
        order: 15,
      },
    ],
  },
];
